import { GalleryFeedbackOptions, TagColors } from '@picflow/types';
import Stripe from 'stripe';
import { ProductPrice } from './types/product.type';

export const getBaseZoom = () => {
  return 1;
};

export const storyblokImageSizes = [2400, 1920, 1280, 640, 0];
export const storyblokLowQualityMap = {
  2400: 85,
  1920: 85,
  1280: 85,
  640: 75,
};

export const assetFilenameSpace = 30;

export const multiPartUploadPartSize = 1024 * 1024 * 10;

export const multiPartUploadConcurrency = 1;

export const downloadSizes: { name: string; size?: number }[] = [
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'small',
    size: 720,
  },
  {
    name: 'medium',
    size: 1920,
  },
  {
    name: 'large',
    size: 3600,
  },
  {
    name: 'original',
  },
];

export const languages: string[] = ['en', 'de', 'fr'];

export const defaultLanguage = 'en';

export const doubleClickTime = 350;

export const biggestGroupTimeDiff = 1000 * 60 * 10;

export const maxAnimatedGifSize = 1024 * 1024 * 3;

export const maxConcurrentUploads = 5;

export const activityPerPage = 50;

export const dragScrollThreshold = 50;

export const assetLoadSettings = {
  galleryScreensToPreloadBefore: 0.5,
  galleryScreensToPreloadAfter: 1,
  galleryScreensToPreGenerate: 5,
  assetPagePreGenerateBefore: 1,
  assetPagePreloadBefore: 1,
  assetPagePreloadAfter: 3,
  assetPagePreGenerateAfter: 10,
  preGenerateHighResFromList: true,
};

export const bigIntDividePresision = 1000000n;

export const assetSizes = [20, 480, 720, 1280, 1920, 2400, 3840, 99999999999999];

export const assetSizesBigToSmall = [...assetSizes].reverse();

export const defaultDarkBackgroundThreshold = 186;

export const trialValueMultiplier = 0.5;

export const thirdPartyScriptDelay = 1000 * 2;

export const tooltipDelay = 300;

export const assetSideMenuSize = 400;

export const maxWatermarkApplyTime = 1000 * 60 * 5;

export const maxUploadRetryCount = 1;
export const maxPostAssetRetryCount = 1;
export const maxUploadFullRetryCount = 1;

// export const maxZipRequestSize = 1; // 1 byte
export const maxZipRequestSize = 1024 * 1024 * 1024 * 1; // 1 GB

export const maxResizeBatchRetryCount = 2;
export const assetsPerResizeBatch = 50;

export const specialCaseFormats = ['png', 'webp', 'gif', 'tif', 'tiff'];

export const resizedImageDownloadFormatMap = {
  webp: 'webp',
  gif: 'gif',
  tif: 'jpg',
  tiff: 'jpg',
  png: 'png',
};

export const browserRenderableExtensions = ['jpg', 'jpeg', 'gif', 'png', 'webp'];
export const allowedTypes = ['jpg', 'jpeg', 'gif', 'png', 'tiff', 'tif', 'webp', 'avif'];
export const allowedVideoTypes = ['mp4', 'mov', 'avi', 'webm', 'm4v', 'mkv'];
export const allowedRawTypes = [
  'nef',
  'raf',
  'dng',
  'cr2',
  'cr3',
  'nrw',
  'arw',
  'pef',
  'ptx',
  '3fr',
  'fff',
  'orf',
  'iiq',
  'rw2',
  'raw',
  'rwl',
  'dcr',
  'heic',
  'heif',
];

export const accessLevelOrder = ['no_access', 'view_only', 'review', 'edit'];

export const maxTimeBetweenReloads = 1000 * 60 * 60 * 12;

export const maxFileSize = 1024 * 1024 * 1024 * 5;
export const maxFileSizeNew = 1024 * 1024 * 1024 * 100;

export const trueUpDurationInDays = 14;

export const msInADay = 1000 * 60 * 60 * 24;

export const debugMaxEntries = 1000;

export const tagColors: TagColors[] = [null, 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink'];

export const fontSizeMap = {
  small: 1,
  medium: 2,
  large: 3,
  huge: 4,
};

export const GbForStoragePrice = (price: Stripe.Price) => {
  if (price.lookup_key.includes('_1tb_')) return 1000;
  return 250;
};

export const allFeedbackOptions: GalleryFeedbackOptions[] = [
  'favourite',
  'comment',
  'status',
  'color_label',
  'annotation',
];

// This is not great.. need to move this to a backend endpoint

export const productsToSell = ['plus', 'pro', 'team', 'enterprise'];

const getBytesFromKilobytes = (kilobytes: bigint): bigint => {
  return kilobytes * 1024n;
};

const getBytesFromMegabytes = (megabytes: bigint): bigint => {
  return getBytesFromKilobytes(megabytes) * 1024n;
};

const getBytesFromGigabytes = (gigabytes: bigint): bigint => {
  return getBytesFromMegabytes(gigabytes) * 1024n;
};

export const extraParamsForCreateGalleryFromPreset = {
  title: '',
  cover: {
    overlay_color: 'rgba(31,35,46,0.2)',
    position: '50% 50%',
    size: 'small',
    uuid: '',
    content_position: <[number, number]>[0.5, 0.5],
  },
};

export const visitorsCacheMaxAge = 1000 * 60 * 5;
export const membersCacheMaxAge = 1000 * 60 * 5;
export const userGroupsMaxAge = 1000 * 60 * 5;
export const galleryMembersMaxAge = 1000 * 60 * 5;

export interface BillingProductSchema {
  core_galleries: number;
  core_storage: number;
  tenant_seats: number;
  scopes: string[];
}

export interface BillingStorageAddonProductSchema {
  core_storage_addon: number;
}

export type BillingPrices = Record<string, BillingProductSchema | BillingStorageAddonProductSchema | object>;

export const concatUniqueArray = <T>(...args: T[][]): T[] => {
  return [...new Set(args.flat())];
};

// every billing scope string, etc. 'asset:favourites'
export type allBillingScopes = keyof (typeof billingScopes)[keyof typeof billingScopes];

type BillingScopes = typeof billingScopes;
type CoreScopes = BillingScopes['core'][keyof BillingScopes['core']];
type AssetScopes = BillingScopes['asset'][keyof BillingScopes['asset']];
type AssetVersionScopes = BillingScopes['assetVersion'][keyof BillingScopes['assetVersion']];
type GalleryScopes = BillingScopes['gallery'][keyof BillingScopes['gallery']];
type GalleryPresetsScopes = BillingScopes['galleryPresets'][keyof BillingScopes['galleryPresets']];
type TenantScopes = BillingScopes['tenant'][keyof BillingScopes['tenant']];
type WatermarkScopes = BillingScopes['watermark'][keyof BillingScopes['watermark']];
type GroupsScopes = BillingScopes['groups'][keyof BillingScopes['groups']];
type GroupUsersScopes = BillingScopes['groupUsers'][keyof BillingScopes['groupUsers']];
type GroupGalleriesScopes = BillingScopes['groupGalleries'][keyof BillingScopes['groupGalleries']];

export type billingScope =
  | CoreScopes
  | AssetScopes
  | AssetVersionScopes
  | GalleryScopes
  | GalleryPresetsScopes
  | TenantScopes
  | WatermarkScopes
  | GroupsScopes
  | GroupUsersScopes
  | GroupGalleriesScopes;

export const billingScopes = {
  core: {
    storageAddons: 'core:storage-addons',
  },
  asset: {
    favourites: 'asset:favourites',
    comments: 'asset:comments',
    flags: 'asset:flags',
    status: 'asset:status',
    colorLabels: 'asset:color-labels',
    customLabels: 'asset:custom-labels',
    upload: 'asset:upload',
    uploadRaw: 'asset:upload-raw',
    download: 'asset:download',
    downloadOriginal: 'asset:download-original',
    watermarking: 'asset:watermarking',
    fileAttachment: 'asset:file-attachment',
    embedRightClickProtection: 'asset:embed-right-click-protection',
    video: 'asset:video',
    createCdnLink: 'asset:create-cdn-link',
    customVideoPoster: 'asset:custom-video-poster',
  },
  assetVersion: {
    multiple: 'asset-version:multiple',
  },
  gallery: {
    password: 'gallery:password',
    subGallery: 'gallery:sub-gallery',
    embedSection: 'gallery:embed-section',
    embedGallery: 'gallery:embed-gallery',
    clientAdvancedMode: 'gallery:client-advanced-mode',
    clientLayoutMode: 'gallery:client-layout-mode',
    approvalLimit: 'gallery:approval-limit',
    removePicflowBranding: 'gallery:remove-picflow-branding',
    customPresets: 'gallery:custom-presets',
    forceIdentification: 'gallery:force-identification',
    approvedDownloadsOnly: 'gallery:approved-downloads-only',
    downloadFromEmbed: 'gallery:download-from-embed',
    videoInCover: 'gallery:video-in-cover',
  },
  galleryPresets: {
    approvedDownloadsOnly: 'gallery-presets:approved-downloads-only',
  },
  tenant: {
    logoUpload: 'tenant:logo-upload',
    customDomain: 'tenant:custom-domain',
    customFonts: 'tenant:custom-fonts',
    seatAddons: 'tenant:seat-addons',
    customGoogleAnalytics: 'tenant:custom-google-analytics',
    blockPublicLinkAccess: 'tenant:block-public-link-access',
    enforce2fa: 'tenant:enforce-2fa',
  },
  watermark: {
    update: 'watermark:update',
    create: 'watermark:create',
  },
  groups: {
    list: 'groups:list',
    create: 'groups:create',
    retrieve: 'groups:retrieve',
    update: 'groups:update',
    delete: 'groups:delete',
  },
  groupUsers: {
    add: 'group-users:add',
    remove: 'group-users:remove',
    list: 'group-users:list',
  },
  groupGalleries: {
    list: 'group-galleries:list',
    attach: 'group-galleries:attach',
    detach: 'group-galleries:detach',
    retrieve: 'group-galleries:retrieve',
    update: 'group-galleries:update',
  },
} as const;

export interface ProductConfig {
  core_galleries: number;
  core_storage: bigint;
  max_tenant_seats: number;
  scopes: billingScope[];
}

const freeProduct = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(2n),
  max_tenant_seats: 2,
  scopes: [
    billingScopes.asset.favourites,
    billingScopes.asset.comments,
    billingScopes.asset.upload,
    billingScopes.asset.download,
    billingScopes.asset.downloadOriginal,
    billingScopes.asset.flags,
    billingScopes.asset.status,
    billingScopes.gallery.clientAdvancedMode,
  ],
};
const basicProduct = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(10n),
  max_tenant_seats: 2,
  scopes: concatUniqueArray(freeProduct.scopes, [
    billingScopes.core.storageAddons,
    billingScopes.asset.colorLabels,
    billingScopes.assetVersion.multiple,
    billingScopes.gallery.subGallery,
    billingScopes.gallery.embedSection,
    billingScopes.gallery.removePicflowBranding,
    billingScopes.gallery.password,
    billingScopes.gallery.forceIdentification,
  ]),
};
const plusProduct = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(100n),
  max_tenant_seats: 5,
  scopes: concatUniqueArray(basicProduct.scopes, [
    billingScopes.tenant.logoUpload,
    billingScopes.gallery.embedGallery,
    billingScopes.gallery.downloadFromEmbed,
    billingScopes.asset.embedRightClickProtection,
    billingScopes.asset.video,
  ]),
};
const proProduct = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(1000n),
  max_tenant_seats: 20,
  scopes: concatUniqueArray(plusProduct.scopes, [
    billingScopes.asset.customLabels,
    billingScopes.asset.uploadRaw,
    billingScopes.asset.watermarking,
    billingScopes.asset.fileAttachment,
    billingScopes.asset.createCdnLink,
    billingScopes.asset.customVideoPoster,
    billingScopes.gallery.clientLayoutMode,
    billingScopes.gallery.approvalLimit,
    billingScopes.gallery.customPresets,
    billingScopes.gallery.approvedDownloadsOnly,
    billingScopes.gallery.videoInCover,
    billingScopes.galleryPresets.approvedDownloadsOnly,
    billingScopes.tenant.customDomain,
    billingScopes.tenant.customFonts,
    billingScopes.tenant.customGoogleAnalytics,
    billingScopes.watermark.create,
    billingScopes.watermark.update,
  ]),
};
const teamProduct = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(2000n),
  max_tenant_seats: -1,
  scopes: concatUniqueArray(proProduct.scopes, [billingScopes.tenant.seatAddons]),
};
const enterpriseProduct = {
  core_galleries: -1,
  core_storage: getBytesFromGigabytes(10000n),
  max_tenant_seats: -1,
  scopes: concatUniqueArray(teamProduct.scopes, [
    billingScopes.groups.list,
    billingScopes.groups.create,
    billingScopes.groups.retrieve,
    billingScopes.groups.update,
    billingScopes.groups.delete,
    billingScopes.groupUsers.add,
    billingScopes.groupUsers.remove,
    billingScopes.groupUsers.list,
    billingScopes.groupGalleries.attach,
    billingScopes.groupGalleries.detach,
    billingScopes.groupGalleries.list,
    billingScopes.groupGalleries.update,
    billingScopes.tenant.blockPublicLinkAccess,
    billingScopes.tenant.enforce2fa,
  ]),
};

export const productConfig: { [key: string]: ProductConfig } = {
  'Picflow Free': freeProduct,
  'Picflow Basic': basicProduct,
  'Picflow Plus': plusProduct,
  'Picflow Pro': proProduct,
  'Picflow Team': teamProduct,
  'Picflow Enterprise': enterpriseProduct,
};

Object.keys(productConfig).forEach(key => {
  if (productConfig[key].max_tenant_seats === -1) {
    productConfig[key].max_tenant_seats = Infinity;
  }
});

export const freeProductPrice = <ProductPrice>{ amount: 0 };
